import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useFeatureFlags } from '../contexts/FeatureFlagContext'
import { isPlatformAdminHostname, isPlatformConsolePath, isTenantSiteBlockedPlatformRoute } from '../config/platformAdminHost'

const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null }) => {
  const { isAuthenticated, hasPermission, can, loading } = useAuth()
  const { isPageEnabled } = useFeatureFlags()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated()) {
    // Redirect to login page with return url
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!isPlatformAdminHostname() && isTenantSiteBlockedPlatformRoute(location.pathname)) {
    return <Navigate to="/dashboard" replace />
  }

  if (isPlatformAdminHostname() && !isPlatformConsolePath(location.pathname)) {
    return <Navigate to="/admin/platform-config" replace />
  }

  // Feature flag check: block access to disabled pages/modules
  if (!isPageEnabled(location.pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-yellow-100 rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Feature Not Available</h2>
          <p className="mt-2 text-gray-600">This feature is not enabled for your organization.</p>
          <p className="mt-1 text-sm text-gray-500">Contact your administrator to enable this feature.</p>
        </div>
      </div>
    )
  }

  // Fine-grained permission takes precedence when specified (split on last ':' for admin/venue-config:manage)
  const requiredPermArgs =
    !requiredPermission
      ? null
      : Array.isArray(requiredPermission)
        ? requiredPermission
        : (() => {
            const s = requiredPermission
            if (typeof s !== 'string') return [String(s), 'view']
            const i = s.lastIndexOf(':')
            if (i <= 0) return [s, 'view']
            return [s.slice(0, i), s.slice(i + 1)]
          })()
  if (requiredPermission && requiredPermArgs && !can(...requiredPermArgs)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
          <p className="mt-1 text-sm text-gray-500">Required permission: {Array.isArray(requiredPermission)? requiredPermission.join(':') : requiredPermission}</p>
        </div>
      </div>
    )
  }

  if (requiredRole && !hasPermission(requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
          <p className="mt-1 text-sm text-gray-500">Required role: {requiredRole}</p>
        </div>
      </div>
    )
  }

  return children
}

export default ProtectedRoute